import React, { useState } from 'react';
import { Card, Form, Input, Button, message, Tabs, Divider } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  MailOutlined, 
  EyeInvisibleOutlined, 
  EyeTwoTone 
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './index.css';

const { TabPane } = Tabs;

const Auth = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const navigate = useNavigate();
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();

  // 生成随机颜色的函数（需要在组件外部定义或提升到作用域内）
  const getRandomColor = () => {
    const colors = ['0D8ABC', '4ECDC4', 'FF6B6B', '45B7D1', '96CEB4', 'FFEAA7', 'DDA0DD'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  // 处理登录
  const handleLogin = async (values) => {
    setLoading(true);
    try {
      // 获取所有注册的用户
      const allUsers = JSON.parse(localStorage.getItem('blog_users') || '[]');
      
      // 查找用户
      const foundUser = allUsers.find(user => 
        user.username === values.username && user.password === values.password
      );

      if (foundUser) {
        // 登录成功
        const userData = {
          id: foundUser.id,
          username: foundUser.username,
          email: foundUser.email,
          avatar: foundUser.avatar,
          bio: foundUser.bio || '这个人很神秘，什么也没留下。',
          registerDate: foundUser.createdAt || new Date().toISOString().split('T')[0]
        };
        
        localStorage.setItem('blog_user', JSON.stringify(userData));
        
        if (onLogin) {
          console.log('调用 onLogin 回调');
          onLogin(userData);
        }
        
        message.success('登录成功！');
        navigate('/');
        return true;
      }
      
      message.error('用户名或密码错误');
      return false;
    } catch (error) {
      console.error('登录失败:', error);
      message.error('登录失败，请重试');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // 处理注册
  const handleRegister = async (values) => {
    setLoading(true);
    try {
      // 验证密码
      if (values.password !== values.confirmPassword) {
        message.error('两次输入的密码不一致！');
        return;
      }

      // 检查用户名是否已存在
      const allUsers = JSON.parse(localStorage.getItem('blog_users') || '[]');
      const usernameExists = allUsers.some(user => user.username === values.username);

      if (usernameExists) {
        message.error('用户名已存在，请换一个用户名');
        return;
      }

      // 生成好看的头像
      const avatar = `https://api.dicebear.com/7.x/avataaars/svg?seed=${values.username}&backgroundColor=${getRandomColor()}`;
      
      // 创建新用户
     // 在注册函数的末尾部分
// 创建新用户
const newUser = {
  id: Date.now(),
  username: values.username,
  email: values.email,
  password: values.password,
  avatar: avatar,
  bio: '这个人很神秘，什么也没留下。',
  techStack: [],
  interests: [],
  github: '',
  twitter: '',
  website: '',
  role: 'user',
  createdAt: new Date().toISOString().split('T')[0]
};

// 保存用户
const updatedUsers = [...allUsers, newUser];
localStorage.setItem('blog_users', JSON.stringify(updatedUsers));

// 自动登录时的用户数据
const userData = {
  id: newUser.id,
  username: newUser.username,
  email: newUser.email,
  avatar: newUser.avatar,
  bio: newUser.bio,
  techStack: newUser.techStack,
  interests: newUser.interests,
  github: newUser.github,
  twitter: newUser.twitter,
  website: newUser.website,
  registerDate: newUser.createdAt,
  role: newUser.role
};
      localStorage.setItem('blog_user', JSON.stringify(userData));
      
      if (onLogin) {
        onLogin(userData);
      }

      message.success('注册成功！已自动登录');
      navigate('/');
    } catch (error) {
      console.error('注册失败:', error);
      message.error('注册失败，请重试！');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <div className="auth-header">
          <h1>我的博客</h1>
          <p>记录生活点滴，分享技术思考</p>
        </div>

        <Divider />

        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          centered
          className="auth-tabs"
        >
          <TabPane tab="登录" key="login">
            <Form
              form={loginForm}
              layout="vertical"
              onFinish={handleLogin}
              className="auth-form"
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[{ required: true, message: '请输入用户名！' }]}
              >
                <Input 
                  placeholder="请输入用户名"
                  prefix={<UserOutlined />}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[{ required: true, message: '请输入密码！' }]}
              >
                <Input.Password
                  placeholder="请输入密码"
                  prefix={<LockOutlined />}
                  size="large"
                  iconRender={(visible) => 
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  block
                  size="large"
                  className="submit-btn"
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab="注册" key="register">
            <Form
              form={registerForm}
              layout="vertical"
              onFinish={handleRegister}
              className="auth-form"
            >
              <Form.Item
                name="username"
                label="用户名"
                rules={[
                  { required: true, message: '请输入用户名！' },
                  { min: 3, max: 20, message: '用户名3-20个字符！' },
                  { pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/, message: '用户名只能包含中文、英文、数字和下划线' }
                ]}
              >
                <Input 
                  placeholder="请输入用户名"
                  prefix={<UserOutlined />}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="邮箱"
                rules={[
                  { required: true, message: '请输入邮箱！' },
                  { type: 'email', message: '请输入有效的邮箱地址！' }
                ]}
              >
                <Input 
                  placeholder="请输入邮箱"
                  prefix={<MailOutlined />}
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="密码"
                rules={[
                  { required: true, message: '请输入密码！' },
                  { min: 6, message: '密码至少6个字符！' }
                ]}
              >
                <Input.Password
                  placeholder="请输入密码"
                  prefix={<LockOutlined />}
                  size="large"
                  iconRender={(visible) => 
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="确认密码"
                rules={[
                  { required: true, message: '请再次输入密码！' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('两次输入的密码不一致！'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  placeholder="请再次输入密码"
                  prefix={<LockOutlined />}
                  size="large"
                  iconRender={(visible) => 
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={loading}
                  block
                  size="large"
                  className="submit-btn"
                >
                  注册账号
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>

        <div className="auth-footer">
          <p>
            {activeTab === 'login' ? '还没有账号？' : '已有账号？'}
            <a onClick={() => setActiveTab(activeTab === 'login' ? 'register' : 'login')}>
              {activeTab === 'login' ? ' 立即注册' : ' 立即登录'}
            </a>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default Auth;